import useUserAccountSettings from "../hooks/useUserAccountSettings";
import {Button, Form, FormSelect, Spinner} from "react-bootstrap";
import {defineMessages, injectIntl} from "react-intl";
import PropTypes from "prop-types";
import useForm from "../hooks/useForm";
import React, {useEffect} from "react";
import useBankAccount from "../hooks/useBankAccount";

const messages = defineMessages({
    bankAccountDetails: {
        id: "Pankkitilin tiedot",
        defaultMessage: "Pankkitilin tiedot"
    },
    updateInfo: {
        id: "Päivitä tiedot",
        defaultMessage: "Päivitä tiedot"
    },
    accountType: {
        id: "Tilityyppi",
        defaultMessage: "Tilityyppi"
    },
    accountNumber: {
        id: "Tilinumero",
        defaultMessage: "Tilinumero"
    },
    accountHolderName: {
        id: "Tilinumeron haltijan nimi",
        defaultMessage: "Tilinumeron haltijan nimi"
    },
    bic: {
        id: "BIC",
        defaultMessage: "BIC"
    },
    iban: {
        id: "IBAN",
        defaultMessage: "IBAN"
    },
    bban: {
        id: "BBAN (Tilinumero)",
        defaultMessage: "BBAN (Tilinumero)"
    },
    bankgiro: {
        id: "BankGiro",
        defaultMessage: "BankGiro"
    },
    plusgirot: {
        id: "PlusGirot",
        defaultMessage: "PlusGirot"
    }
});

const SettingsBankAccount = (props) => {
    const { settings } = useUserAccountSettings();
    const { dirty, form, handleChange, validateForm, updateForm } = useForm(settings);
    const { getSupportedBankTypes } = useBankAccount();
    const [ accountTypes, setAccountTypes ] = React.useState([]);

    const lng = props.intl.formatMessage;

    useEffect(() => {
        const fetchBankTypes = async () => {
            try {
                const types = await getSupportedBankTypes();
                setAccountTypes(types);
            } catch (error) {
                console.error("Error fetching bank types:", error);
                setAccountTypes([]);
            }
        }
        void fetchBankTypes();
    }, []);

    useEffect(() => {
        if (form.bankAccountType === '') {
            updateForm({ ...form, bankAccountNumber: '', bankAccountBic: '', bankAccountHolderName: '' });
        } else if (form.bankAccountType === 'iban' && form.bankAccountNumber?.startsWith('FI')) {
            updateForm({ ...form, bankAccountBic: '' });
        }
    }, [form.bankAccountType, form.bankAccountNumber]);
    
    const handleSubmit = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (validateForm(event.target)) {
            props.onSubmit(form);
        }
    }

    // Check if BIC is required based on the selected bank account type
    // BIC is required only for IBAN accounts in most countries, but not in Finland
    // In Finland, BIC is resolved from IBAN, so we don't require it separately
    const bicRequired = form.bankAccountType === 'iban' && !form.bankAccountNumber?.startsWith('FI');

    return (
        <div>
            <h2>{lng(messages.bankAccountDetails)}</h2>
            <Form id='settings-bank-account-form' onSubmit={handleSubmit} noValidate>
                <Form.Floating className="mb-2 pb-2">
                    <FormSelect
                        name='bankAccountType'
                        className="form-control"
                        onChange={handleChange}
                        value={ form.bankAccountType }
                    >
                        {['', ...accountTypes].map((option) => (
                            <option key={option} value={option}>
                                {option !== '' ? lng(messages[option]): '-'}
                            </option>
                        ))}
                    </FormSelect>
                    <Form.Label htmlFor="bankAccountType">{lng(messages.accountType)}</Form.Label>
                </Form.Floating>
                <Form.Floating className="mb-2 pb-2">
                    <Form.Control
                        required={form.bankAccountType !== ''}
                        name='bankAccountNumber'
                        value={form.bankAccountNumber}
                        onChange={handleChange}
                        disabled={!form.bankAccountType || form.bankAccountType === ''}
                    />
                    <Form.Label htmlFor="bankAccountNumber">{lng(messages.accountNumber)}</Form.Label>
                </Form.Floating>
                <Form.Floating className="mb-2 pb-2">
                    <Form.Control
                        required={bicRequired}
                        name='bankAccountBic'
                        value={form.bankAccountBic}
                        onChange={handleChange}
                        disabled={!bicRequired}
                    />
                    <Form.Label htmlFor="bankAccountBic">{lng(messages.bic)}</Form.Label>
                </Form.Floating>
                <Form.Floating className="mb-2 pb-2">
                    <Form.Control
                        required={form.bankAccountType !== ''}
                        name='bankAccountHolderName'
                        value={form.bankAccountHolderName}
                        onChange={handleChange}
                        disabled={!form.bankAccountType || form.bankAccountType === ''}
                    />
                    <Form.Label htmlFor="bankAccountHolderName">{lng(messages.accountHolderName)}</Form.Label>
                </Form.Floating>
                <Button
                    form="settings-bank-account-form"
                    variant="primary"
                    type="submit"
                    disabled={props.loading || !dirty}
                >
                    {props.loading && (<Spinner animation="border" size="sm" />)} {lng(messages.updateInfo)}
                </Button>
            </Form>
        </div>
    )
}

SettingsBankAccount.propTypes = {
    'intl': PropTypes.object.isRequired,
    'loading': PropTypes.bool.isRequired,
    'onSubmit': PropTypes.func.isRequired
};

export default injectIntl(SettingsBankAccount);
